/**
 * Utility functions for content formatting
 */

import { marked } from 'marked';
import {
  prepareProtocolHeadingContent,
  promoteProtocolSubsectionHeadingsInHtml,
  joinFragmentedHeadingContinuationInHtml,
} from './protocolExportContent';
import { isLikelyProtocolHeading } from './protocolHeadingLineUtils';
import { applyGrammarGuardsToHtml } from './grammarGuards';
import { applyProtocolTypography } from './protocolTypography';

/**
 * GFM pipe tables require newlines between rows. Generators or editors sometimes merge rows into
 * a single line using "||" (e.g. "| Description ||---|---|| **Population** | ...").
 * Expands those into "| ... |\n|---|\n| ..." so parsers and exports see a valid table.
 */
export function normalizeCollapsedMarkdownTablePipes(content) {
  if (content == null || typeof content !== 'string') return content || '';
  let s = content;
  if (!s.includes('||')) return s;
  if (!/\|/.test(s)) return s;
  let prev = null;
  while (prev !== s) {
    prev = s;
    s = s.replace(/\|\|/g, '|\n|');
  }
  return s;
}

/**
 * Inserts a newline before a GFM separator row when it was glued to the header row
 * (e.g. "| Col | Col | |---|---|" → "| Col | Col |\n|---|----|").
 */
export function normalizeMarkdownTableHeaderSeparatorRun(content) {
  if (content == null || typeof content !== 'string') return content || '';
  let s = content;
  if (!s.includes('|') || !/-{3,}/.test(s)) return s;
  let prev = null;
  while (prev !== s) {
    prev = s;
    s = s.replace(/\|\s+\|(-{3,}(?:\|[:\-|]{3,})*)/g, '|\n|$1');
  }
  return s;
}

/**
 * When plain text loses newlines, "# Heading This paragraph..." stays on one line; marked then
 * treats the whole line as heading text. Split before a typical sentence start after ATX heading.
 */
function splitGluedAtxHeadingFromBody(md) {
  return md.replace(
    /^(#{1,6}\s+[^\n]+?)(\s+(?:This|These|The|In accordance|According to)\b)/gm,
    '$1\n\n$2',
  );
}

/**
 * Converts markdown / plain protocol text to HTML for post-autofix persistence.
 * Uses GFM (headings, pipe tables, lists) instead of paragraph-only splitting, which was
 * flattening "# Title" and table rows into a single line.
 */
export function markdownPlainTextToHtml(text) {
  if (text == null || typeof text !== 'string') return '';
  let md = String(text).trim();
  if (!md) return '';
  md = normalizeCollapsedMarkdownTablePipes(md);
  md = normalizeMarkdownTableHeaderSeparatorRun(md);
  md = splitGluedAtxHeadingFromBody(md);
  try {
    marked.setOptions({
      gfm: true,
      breaks: false,
    });
    return marked.parse(md);
  } catch (e) {
    console.warn('[markdownPlainTextToHtml] marked.parse failed:', e);
    const esc = (s) =>
      String(s)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    return md
      .split(/\n{2,}/)
      .map((block) => `<p>${esc(block).replace(/\n/g, '<br/>')}</p>`)
      .join('');
  }
}

/**
 * Converts Markdown asterisks to HTML formatting
 * @param {string} content - The content with Markdown formatting
 * @returns {string} - Content with HTML formatting
 */
export const convertMarkdownToHTML = (content) => {
  if (!content) return '';
  
  return content
    // Convert **text** to <strong>text</strong> (bold)
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    // Convert *text* to <em>text</em> (italic) - but be careful not to match **text**
    .replace(/(?<!\*)\*(?!\*)(.*?)(?<!\*)\*(?!\*)/g, '<em>$1</em>')
    // Convert # Heading to <h1>Heading</h1>
    .replace(/^# (.*$)/gm, '<h1>$1</h1>')
    // Convert ## Heading to <h2>Heading</h2>
    .replace(/^## (.*$)/gm, '<h2>$1</h2>')
    // Convert ### Heading to <h3>Heading</h3>
    .replace(/^### (.*$)/gm, '<h3>$1</h3>')
    // Convert #### Heading to <h4>Heading</h4>
    .replace(/^#### (.*$)/gm, '<h4>$1</h4>')
    // Convert - item to <li>item</li> (bullet points)
    .replace(/^- (.*$)/gm, '<li>$1</li>')
    // Convert 1. item to <li>item</li> (numbered lists)
    .replace(/^\d+\. (.*$)/gm, '<li>$1</li>')
    // Wrap consecutive <li> elements in <ul> or <ol>
    .replace(/(<li>.*<\/li>)/gs, (match) => {
      const items = match.match(/<li>.*?<\/li>/g);
      if (items && items.length > 0) {
        // Check if it's a numbered list (contains digits)
        const isNumbered = items.some(item => /^\d+\./.test(item.replace(/<li>|<\/li>/g, '')));
        const tag = isNumbered ? 'ol' : 'ul';
        return `<${tag}>${match}</${tag}>`;
      }
      return match;
    });
};

/**
 * Inserts a line break before an ATX markdown heading marker (##, ###, ...)
 * that the LLM glued directly onto the end of the previous line/heading with
 * no newline (e.g. "## Title ### Next Heading" all on one line) — otherwise
 * a real markdown parser (marked) or the line-anchored regexes below treat
 * the whole thing as one heading's text instead of two separate headings.
 * General to ANY following text (unlike splitGluedAtxHeadingFromBody above,
 * which only splits before a fixed list of words) since a glued heading
 * marker is unambiguous on its own — no need to guess what comes after it.
 * [^\n#] (not just [^\n]) so this can't self-match INSIDE a single "### "
 * run (treating the 1st '#' as "preceding text" and "## " as the marker,
 * which would wrongly split "### 5.2" into "#" + "## 5.2"). Requires 2+
 * consecutive '#' so a literal single '#' used as a number sign (e.g.
 * "Grade # 3") is left untouched.
 */
export function splitGluedMarkdownHeadingMarkers(content) {
  const text = String(content || '');
  if (!/#{2,6}\s/.test(text)) return text;
  return text.replace(/([^\n#])(#{2,6}\s+)/g, '$1\n\n$2');
}

/**
 * Converts literal ATX markdown heading markers (#, ##, ###, ####) to the
 * matching <hN> tag, even when they're mixed into content that already has
 * real HTML elsewhere — processContentForDisplay's hasHTML branch (and the
 * equivalent check in DocumentEditor.jsx's edit-mode renderer) otherwise
 * skip convertMarkdownToHTML entirely whenever ANY HTML tag is present,
 * which left raw '#' characters visible whenever the LLM partially followed
 * the "use HTML" formatting instruction but still labeled some subsections
 * with markdown headings instead.
 */
export function convertStrayMarkdownHeadingsToHtml(content) {
  const text = splitGluedMarkdownHeadingMarkers(content);
  return text
    .replace(/^#### (.*)$/gm, '<h4>$1</h4>')
    .replace(/^### (.*)$/gm, '<h3>$1</h3>')
    .replace(/^## (.*)$/gm, '<h2>$1</h2>')
    .replace(/^# (.*)$/gm, '<h1>$1</h1>');
}

// Only tag names that are unambiguous as HTML — never real English words or
// clinical shorthand that could legitimately sit glued to '>' (e.g. "p" in
// "p>0.05", or "a"/"b"/"i" as single letters) are deliberately excluded.
const ORPHANED_OPENING_TAG_NAMES = 'li|ul|ol|strong|em|h[1-6]|table|thead|tbody';

/**
 * Repairs a narrow, observed LLM generation slip — an opening tag missing
 * its leading '<' (e.g. "li>Some bullet text</li>" instead of
 * "<li>Some bullet text</li>"), seen in long repetitive lists where the
 * model drops one '<' among many near-identical "<li>" tokens. Confirmed
 * (via investigation) this isn't something our own pipeline strips — it's
 * LLM output noise, same class of problem as convertStrayMarkdownHeadingsToHtml
 * above, fixed the same way: a deterministic display-layer repair rather
 * than relying on the LLM-judged validator to catch a single dropped
 * character.
 *
 * The negative lookbehind excludes both '<' (real opening tag — nothing to
 * fix) and '/' (a real closing tag, e.g. "</li>" — its "li>" must NOT be
 * rewritten to "</<li>"). Combined with \b, this also won't fire inside an
 * ordinary word ("protocol>" has no boundary before its trailing "ol").
 *
 * Also matches the HTML-entity-escaped form "li&gt;" (not just the literal
 * "li>") — confirmed via the live database that this is what's actually
 * stored: the server-side Mongoose setter for `output`
 * (etmf-server/src/utils/sanitizeHtml.js's sanitizeServerHtml, backed by the
 * sanitize-html package) parses "li>text" as a text node (no leading '<' —
 * nothing there for an HTML parser to recognize as a tag), then re-escapes
 * the literal '>' inside that text node to '&gt;' when serializing its
 * sanitized output. So by the time this reaches the client, the character
 * this repair was originally written to find (a literal '>') no longer
 * exists — it's already been turned into the 4-character entity.
 */
export function repairOrphanedOpeningTags(content) {
  const text = String(content || '');
  const re = new RegExp(`(?<![</])\\b(${ORPHANED_OPENING_TAG_NAMES})(?:>|&gt;)`, 'g');
  if (!re.test(text)) return text;
  return text.replace(new RegExp(`(?<![</])\\b(${ORPHANED_OPENING_TAG_NAMES})(?:>|&gt;)`, 'g'), '<$1>');
}

/**
 * Adds formatting instructions to AI prompts
 * @param {string} prompt - The original prompt
 * @returns {string} - Prompt with formatting instructions
 */
export const addFormattingInstructions = (prompt) => {
  const formattingInstructions = `

IMPORTANT FORMATTING INSTRUCTIONS:
- Use HTML tags for formatting instead of Markdown asterisks
- Use <strong>text</strong> for bold text instead of **text**
- Use <em>text</em> for italic text instead of *text*
- Use <h1>, <h2>, <h3>, <h4> for headings instead of # ## ### ####
- Use <ul><li>item</li></ul> for bullet points instead of - item
- Use <ol><li>item</li></ol> for numbered lists instead of 1. item
- Use <p>text</p> for paragraphs
- Use <br> for line breaks when needed
- Use <table><tr><td>content</td></tr></table> for tables
- Use <blockquote>text</blockquote> for quotes

CRITICAL: For section titles and important text, use <strong> tags to make them bold. For example:
- Section titles: <h2><strong>6.0 TRIAL INTERVENTIONS & PRODUCT INFORMATION</strong></h2>
- Subsection titles: <h3><strong>6.1 Description of Investigational Trial Intervention</strong></h3>
- Important labels: <strong>Investigational Agent:</strong> Nadofaragene firadenovec

Please format your response using HTML tags as shown above.

---
# TABLES / FIGURES — IN-TEXT CROSS-REFERENCE REQUIREMENTS (ALL SECTIONS)

These rules apply to EVERY table and EVERY figure generated in this section.

1. **Number and caption every table/figure** using the section-based label format:
   - Tables: \`Table {mainSectionNumber}-{ordinal}: Descriptive Title\`
     (example for Section 12: \`Table 12-1: Summary of Protocol Amendments for ARN-509-003\`)
   - Figures: \`Figure {mainSectionNumber}-{ordinal}: Descriptive Title\`
   - Ordinals restart at 1 within the section and increase for each subsequent table/figure.
2. Place the caption **immediately before** the table/figure (as its own paragraph / heading line).
3. **In-text cross-reference required:** Immediately before each table/figure, write **one sentence that starts with the label** and describes what that table/figure contains (from its caption/title).
   - Preferred style: "**Table 9-1 summarizes the reporting requirements for safety events in this study.**"
   - Preferred style: "**Table 12-1 summarizes the protocol amendments for ARN-509-003.**"
   - Preferred style: "**Figure 1-1 shows the trial schema.**"
   - Bad / forbidden: "Key details are summarized in Table …", "as shown below", "the table below", "X is provided in Table …" (label must lead the sentence).
4. The cross-reference sentence must be **specific to that table/figure’s content/title**, not a repeated stock phrase for every table.
5. Reuse the same label whenever referring to that table/figure again.
6. Do not invent tables/figures you do not include; every numbered cross-reference must have a matching captioned table/figure in the section.
7. Glossary / pure reference lists may still be tables — still caption them and include one title-specific formal in-text cross-reference before the table.`;

  return String(prompt || '') + formattingInstructions;
};

/**
 * Processes content for display in the canvas
 * @param {string} content - The raw content from AI
 * @returns {string} - Processed content ready for display
 */
/**
 * "N. Title" (bare number, no dot — e.g. a local sub-topic label like "1. Randomization
 * Methods and Procedures") is ambiguous with an ordinary numbered list item, so
 * convertMarkdownToHTML would otherwise silently absorb it into an <ol><li> (stripping the
 * number and leaving it sized like body text). Must run BEFORE convertMarkdownToHTML, since
 * that function's own `^\d+\. ` rule would otherwise consume the line first.
 *
 * Reuses the Word export's own heading heuristic (isLikelyProtocolHeading, kind: 'main') —
 * title-case-banner ratio, prose-start rejection, modal-verb/punctuation-density checks — so
 * only lines that actually look like a heading get promoted, not real numbered lists/instructions
 * (e.g. "1. Take drug X. 2. Wait 30 minutes." fails the banner/punctuation checks and is left
 * as a normal list item).
 */
const promoteBareNumberSubHeadings = (text) =>
  String(text || '').replace(/^(\d+)\.\s+([A-Z][^\n]{0,140})$/gm, (match, num, title) => {
    const cleanTitle = title.trim();
    if (!isLikelyProtocolHeading(num, cleanTitle, { kind: 'main' })) return match;
    return `<h4><strong>${num}. ${cleanTitle}</strong></h4>`;
  });

/**
 * Collapse repeated "Table N-M: Title" / "Figure N: Title" caption lines down to the first
 * occurrence, for content that already has duplicates baked in from a prior processing pass.
 * Complements the fix in processMediaBlocks (which stops NEW duplicates from accumulating) by
 * cleaning up ones that already exist in stored content.
 */
const stripDuplicateCaptionLines = (text) => {
  const raw = String(text || '');
  if (!raw) return raw;
  const seen = new Set();
  return raw
    .split('\n')
    .filter((line) => {
      const m = line
        .trim()
        .match(/^(?:<p[^>]*>\s*)?(?:<strong>\s*)?(Table|Figure)\s+(\d+(?:[.-]\d+)*)\s*[:.]?\s*([^<\n]*)/i);
      if (!m) return true;
      const key = `${m[1].toLowerCase()}-${m[2]}-${m[3].trim().toLowerCase()}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .join('\n');
};

const stripInlineHtmlTags = (s) => String(s || '').replace(/<[^>]*>/g, '').trim();

/**
 * Word-set overlap between two normalized heading-shaped strings — deliberately looser than
 * exact-match, since the AI restates its own heading with small wording drift (e.g. "...from
 * the Trial" vs "...from Trial", or "Objective(s)" vs "Objective 1").
 */
const headingWordOverlapRatio = (a, b) => {
  const normalize = (s) =>
    String(s || '')
      .toLowerCase()
      .replace(/[^a-z0-9.\s]/g, ' ')
      .split(/\s+/)
      .filter((w) => w.length > 2);
  const wordsA = new Set(normalize(a));
  const wordsB = new Set(normalize(b));
  if (!wordsA.size || !wordsB.size) return 0;
  let overlap = 0;
  wordsA.forEach((w) => {
    if (wordsB.has(w)) overlap += 1;
  });
  return overlap / Math.min(wordsA.size, wordsB.size);
};

/**
 * Drop a numbered heading line when it's a near-duplicate restatement of the heading that
 * immediately precedes it — the AI's habit of re-stating "7.2 Participant Discontinuation or
 * Withdrawal from Trial" as a plain first line right under the real "7.2 Participant
 * Discontinuation or Withdrawal from the Trial" heading. Unlike isDuplicateSectionHeadingText
 * (protocolExportContent.js), which is scoped to "does this line restate the *parent* section"
 * and deliberately excludes subsection-vs-subsection comparisons, this specifically targets a
 * subsection heading restating itself, and works on already-HTML content too (tag-stripped
 * before comparison) since the canvas often receives the real heading pre-tagged as HTML while
 * the restatement arrives as plain text alongside it.
 */
const stripDuplicateHeadingRestatements = (text) => {
  const raw = String(text || '');
  if (!raw.trim()) return raw;

  const headingRe = /^(\d+(?:\.\d+){1,3})\s+([A-Z].{0,140})$/;
  const lines = raw.split('\n');
  const out = [];
  let lastHeadingPlain = null;
  let linesSinceHeading = 0;

  for (const line of lines) {
    const plain = stripInlineHtmlTags(line);
    const match = plain.match(headingRe);

    if (match) {
      if (
        lastHeadingPlain &&
        linesSinceHeading <= 2 &&
        match[1] === lastHeadingPlain.number &&
        headingWordOverlapRatio(match[2], lastHeadingPlain.title) >= 0.6
      ) {
        continue; // drop the restatement, keep the real (usually already-tagged) heading above it
      }
      lastHeadingPlain = { number: match[1], title: match[2] };
      linesSinceHeading = 0;
      out.push(line);
      continue;
    }

    if (plain) linesSinceHeading += 1;
    out.push(line);
  }

  return out.join('\n');
};

/**
 * Convert a ```mermaid fenced code block — the AI's primary output form for the liver-event
 * flowchart per the section-7 generation prompt — into the <pre class="mermaid"> element
 * DocumentCanvas's Mermaid hydration looks for (querySelectorAll('pre.mermaid, pre.liver-event-mermaid')).
 * Without this, a fenced block never becomes a diagram — it just renders as literal backtick text,
 * since nothing else in this pipeline (convertMarkdownToHTML, the hasHTML pass-through branch)
 * handles code fences at all.
 */
export const convertMermaidFencesToPre = (text) => {
  const raw = String(text || '');
  if (!raw.includes('```')) return raw;
  return raw.replace(/```mermaid\s*\n([\s\S]*?)```/gi, (match, source) => {
    const escaped = source
      .trim()
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    return `<pre class="mermaid">${escaped}</pre>`;
  });
};

export const processContentForDisplay = (content) => {
  if (!content) return '';

  // Keep markdown table content untouched so ReactMarkdown can parse it.
  // Converting it to HTML here makes pipes render as plain text.
  const deduped = convertMermaidFencesToPre(
    stripDuplicateHeadingRestatements(stripDuplicateCaptionLines(String(content))),
  );
  const text = normalizeCollapsedMarkdownTablePipes(deduped);
  const hasMarkdownTable = /^\s*\|.+\|\s*$/m.test(text) && /^\s*\|?\s*:?-{3,}/m.test(text);
  if (hasMarkdownTable) {
    return text;
  }
  
  // Check if content already contains HTML tags
  const hasHTML = deduped.includes('<') && deduped.includes('>');

  if (hasHTML) {
    // If content already has HTML, clean it up and ensure proper formatting
    let processed = deduped
      // Clean up any remaining asterisks in HTML content
      .replace(/\*\*/g, '')
      .replace(/\*/g, '')
      // Ensure proper spacing around HTML tags
      .replace(/>\s*</g, '>\n<')
      // Fix any malformed HTML
      .replace(/<ul>\s*<ul>/g, '<ul>')
      .replace(/<\/ul>\s*<\/ul>/g, '</ul>');

    // Content can mix real HTML with literal markdown ATX headings (the LLM
    // partially followed the "use HTML" instruction) — the cleanup above
    // only touches HTML, so convert any stray '#'/'##'/'###' markers too
    // instead of leaving them visible as raw text.
    processed = convertStrayMarkdownHeadingsToHtml(processed);
    // And repair any opening tag the LLM dropped the leading '<' from
    // (e.g. "li>" instead of "<li>") — see repairOrphanedOpeningTags's doc
    // comment.
    return repairOrphanedOpeningTags(processed);
  }

  // For non-HTML content, convert Markdown to HTML
  let processed = convertMarkdownToHTML(promoteBareNumberSubHeadings(deduped));
  
  // Auto-detect and format section titles that don't have formatting.
  // Level is derived from the dot-count in the leading section number (matches the Word
  // export's outlineLevelForProtocolHeadingText), not from word casing — clinical titles
  // routinely contain parentheses/abbreviations ("Overall Survival (OS)") that the old
  // letter-only character classes couldn't match, silently mis-leveling or dropping them.
  processed = processed
    // "N.N", "N.N.N", "N.N.N.N" + title (no period, to avoid matching full sentences).
    // Requires at least one dot (no bare "6 patients...") and an uppercase first letter
    // (no "3.2 mg/kg was administered..." dosage sentences).
    .replace(/^(\d+(?:\.\d+){1,3})\s+([A-Z][^\n.]{0,140})$/gm, (match, num, title) => {
      const dotCount = (num.match(/\./g) || []).length;
      const tag = dotCount === 1 ? 'h2' : dotCount === 2 ? 'h3' : 'h4';
      return `<${tag}><strong>${num} ${title.trim()}</strong></${tag}>`;
    })
    // Format bullet points that start with bold text
    .replace(/^\*\s+\*\*([^*]+)\*\*:\s*(.*)$/gm, '<li><strong>$1:</strong> $2</li>')
    // Format regular bullet points
    .replace(/^\*\s+(.*)$/gm, '<li>$1</li>')
    // Clean up any remaining asterisks that weren't converted
    .replace(/\*\*/g, '')
    .replace(/\*/g, '');
  
  // If content already contains HTML tags, use as is
  if (processed.includes('<strong>') || processed.includes('<h1>') || processed.includes('<p>')) {
    return processed;
  }
  
  // Otherwise, wrap plain text in paragraphs
  return processed.split('\n\n').map(paragraph => {
    if (paragraph.trim()) {
      return `<p>${paragraph.trim()}</p>`;
    }
    return '';
  }).join('');
};

const escapeTableCell = (value) => {
  return String(value || '')
    .replace(/\|/g, '\\|')
    .replace(/\r?\n/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const escapeHtml = (value) => {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

const hasExistingTable = (content) => {
  if (!content) return false;
  const text = String(content);
  const hasHtmlTable = /<table[\s\S]*?>[\s\S]*?<\/table>/i.test(text);
  const hasMarkdownTable = /^\s*\|.+\|\s*$/m.test(text) && /^\s*\|?\s*:?-{3,}/m.test(text);
  return hasHtmlTable || hasMarkdownTable;
};

const getDefaultRowsForSection = (sectionTitle = '') => {
  const title = String(sectionTitle || '').toLowerCase();
  if (title.includes('protocol summary')) {
    return ['Study Identifier', 'Primary Objective', 'Secondary Objectives', 'Intervention', 'Population', 'Control', 'Randomization', 'Duration', 'Sample Size'];
  }
  if (title.includes('background')) {
    return ['Disease Context', 'Standard of Care', 'Unmet Need', 'Rationale'];
  }
  if (title.includes('study assessments') || title.includes('assessments')) {
    return ['Screening/Baseline', 'Efficacy', 'Safety', 'Comments'];
  }
  if (title.includes('study treatments') || title.includes('trial intervention')) {
    return ['Regimen', 'Dose/Frequency', 'Duration', 'Control Arm', 'Concomitant Medications'];
  }
  if (title.includes('regulatory') || title.includes('ethical')) {
    return ['Regulatory Requirements', 'Ethical Considerations', 'Data Governance', 'Oversight'];
  }
  return ['Summary', 'Details', 'Notes'];
};

const buildEmptySectionTable = (sectionTitle = '') => {
  const rows = getDefaultRowsForSection(sectionTitle).map((key) => ({
    key: escapeTableCell(key),
    value: '',
  }));
  return buildHtmlTable(rows);
};

const isTransientPlaceholder = (content) => {
  const text = String(content || '').trim().toLowerCase();
  return (
    text === 'generating content...' ||
    text === 'generating content' ||
    text === 'loading...' ||
    text === 'loading' ||
    text.startsWith('generating content for ')
  );
};

const stripMarkdownTableBlocks = (content) => {
  if (!content) return '';
  const lines = String(content).replace(/\r\n/g, '\n').split('\n');
  const kept = [];
  let i = 0;

  while (i < lines.length) {
    const current = lines[i].trim();
    const next = (lines[i + 1] || '').trim();

    const looksLikeHeaderRow = /^\|.*\|$/.test(current);
    const looksLikeDividerRow = /^\|?\s*:?-{3,}.*\|?\s*$/.test(next);

    if (looksLikeHeaderRow && looksLikeDividerRow) {
      i += 2;
      while (i < lines.length && /^\|.*\|$/.test(lines[i].trim())) {
        i += 1;
      }
      continue;
    }

    if (/^\|.*\|$/.test(current)) {
      i += 1;
      continue;
    }

    kept.push(lines[i]);
    i += 1;
  }

  return kept.join('\n');
};

const buildHtmlTable = (rows = []) => {
  const body = rows.map((row) => {
    return `<tr><td>${escapeHtml(row.key)}</td><td>${escapeHtml(row.value)}</td></tr>`;
  }).join('');

  return `<table><thead><tr><th>Parameter</th><th>Details</th></tr></thead><tbody>${body}</tbody></table>`;
};

const isPlainKeyValueConvertible = (raw) => {
  const text = String(raw || '').trim();
  if (!text) return false;
  if (/<\w+[^>]*>/.test(text)) return false;

  const lines = text
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)
    // Section/subsection headings are not content rows — ignore for convertibility
    .filter((l) => !/^\d+(\.\d+)*\s+[A-Za-z]/.test(l))
    .filter((l) => !/^#{1,6}\s+/.test(l));

  if (!lines.length) return false;

  let kvLike = 0;
  for (const line of lines) {
    if (line.includes('|') || line.includes('\t') || /^([^:]{2,}):\s*(.+)$/.test(line)) {
      kvLike += 1;
    }
  }

  return kvLike / lines.length >= 0.5;
};

/**
 * Converts section content into a deterministic default markdown table.
 * This is code-only formatting (no LLM prompt dependency).
 */
export const convertContentToDefaultTable = (content, sectionTitle = '') => {
  if (isTransientPlaceholder(content)) return String(content || '');
  if (!content || !String(content).trim()) return buildEmptySectionTable(sectionTitle);

  const original = String(content).replace(/\r\n/g, '\n').trim();
  const hasHtmlTable = /<table[\s\S]*?>[\s\S]*?<\/table>/i.test(original);
  if (hasHtmlTable) return content;
  const hasHtmlTags = /<\w+[^>]*>/.test(original);
  if (hasHtmlTags) return content;
  if (!isPlainKeyValueConvertible(original)) return content;

  let raw = original;
  if (hasExistingTable(original)) {
    const withoutTables = stripMarkdownTableBlocks(original).trim();
    if (withoutTables.length > 80) {
      raw = withoutTables;
    } else {
      return content;
    }
  }

  const lines = raw
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .filter((line) => !/^#{1,6}\s+/.test(line))
    .filter((line) => !/^\|.*\|$/.test(line));

  if (lines.length === 0) return content;

  const rows = [];

  lines.forEach((line) => {
    const clean = line.replace(/^(\d+[\).\s]+|[-*]\s+)/, '').trim();

    if (/^\d+(\.\d+)*\s+[A-Za-z]/.test(clean)) {
      return;
    }

    if (clean.includes('|')) {
      const cells = clean
        .split('|')
        .map((c) => c.trim())
        .filter(Boolean);
      if (cells.length >= 2) {
        rows.push({
          key: escapeTableCell(cells[0]),
          value: escapeTableCell(cells.slice(1).join(' | ')),
        });
        return;
      }
    }

    if (clean.includes('\t')) {
      const cells = clean
        .split('\t')
        .map((c) => c.trim())
        .filter(Boolean);
      if (cells.length >= 2) {
        rows.push({
          key: escapeTableCell(cells[0]),
          value: escapeTableCell(cells.slice(1).join(' ')),
        });
        return;
      }
    }

    const kvMatch = clean.match(/^([^:]{2,}):\s*(.+)$/);
    if (kvMatch) {
      rows.push({
        key: escapeTableCell(kvMatch[1]),
        value: escapeTableCell(kvMatch[2]),
      });
    }
  });

  if (rows.length === 0) return content;

  return buildHtmlTable(rows);
};

/**
 * Returns table-first display content for document sections.
 */
export const getTableFirstDisplayContent = (content, sectionTitle = '') => {
  if (isTransientPlaceholder(content)) return String(content || '');
  return convertContentToDefaultTable(content, sectionTitle);
};

// HTML-tag-aware like isOverallDesignContext below — the model's heading may be wrapped
// in <h1>-<h6> (or <p>/<strong>), not just markdown '#'. Missing that alternative here
// previously made this regex fail to locate the heading in HTML-formatted content, so
// ensureOverallDesignTableInContent silently skipped table conversion entirely while the
// (separately HTML-aware) caption cleanup below it still ran — leaving a raw markdown
// table on screen with an otherwise-cleaned-up caption above it.
const OVERALL_DESIGN_HEADING_RE =
  /^(?:#{1,6}\s*|<(?:h[1-6]|p|strong)[^>]*>\s*)?(?:\d+(?:\.\d+)*\s+)?Overall\s+Design\b.*$/i;
const NEXT_PROTOCOL_HEADING_RE =
  /^(?:#{1,6}\s*|<(?:h[1-6]|p|strong)[^>]*>\s*)?\d+(?:\.\d+)*\s+\S/;

// Previously-mandatory lead-in for 1.1.2 Overall Design, dropped: it duplicated the
// "Table 1-1 summarizes…" cross-reference sentence that processMediaBlocks always adds,
// producing a redundant 3-line block (intro + cross-ref + caption) before the table.
// Kept here only to strip stray copies already baked into previously-generated content.
// Not global — safe to reuse across repeated .test() calls (a global regex's lastIndex
// would otherwise carry over between calls and make .test() alternate true/false).
// Callers needing a global .replace() build their own copy from .source below.
const OVERALL_DESIGN_INTRO_RE =
  /key\s+aspects\s+of\s+the\s+trial\s+design\s+are\s+summari[sz]ed\s+below\.?/i;

const overallDesignCrossRefSentence = (label) =>
  `${label} summarizes the key aspects of the overall trial design.`;

const ensureOverallDesignIntroBeforeTable = (block) => {
  const text = String(block || '').trim();
  if (!text) return text;
  // Strip the retired intro line (bare or wrapped in a block tag) wherever it appears —
  // legacy content may have it duplicated or out of position.
  return text
    .replace(new RegExp(`<(p|h[1-6]|div|li)\\b[^>]*>\\s*${OVERALL_DESIGN_INTRO_RE.source}\\s*<\\/\\1>`, 'gi'), '')
    .replace(new RegExp(OVERALL_DESIGN_INTRO_RE.source, 'gi'), '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

const parseOverallDesignKeyValueRows = (blockText) => {
  const lines = String(blockText || '')
    .replace(/\r\n/g, '\n')
    .split('\n')
    .map((l) => l.replace(/\t/g, '  ').trimEnd())
    .filter((l) => l.trim().length > 0);

  const rows = [];
  let pendingParent = null;

  const pushRow = (key, value) => {
    const k = String(key || '').replace(/:$/, '').trim();
    const v = String(value || '').trim();
    if (!k) return;
    rows.push({ key: escapeTableCell(k), value: escapeTableCell(v) });
  };

  for (let i = 0; i < lines.length; i += 1) {
    const raw = lines[i];
    const trimmed = raw.trim();
    if (OVERALL_DESIGN_HEADING_RE.test(trimmed)) continue;
    if (/^\|?\s*:?-{3,}/.test(trimmed)) continue;
    // Skip a stray caption/cross-reference line left in the body from previously-generated
    // content (e.g. "<p><strong>Table 1-1:</strong> Overall Design</p>" or "Table 1-1
    // summarizes…") — checked with HTML tags stripped first, since the caption/cross-ref
    // processMediaBlocks inserts is wrapped in <p>/<strong> and the colon inside "Table
    // 1-1:</strong>" would otherwise match the generic "key: value" pattern below and
    // become a bogus table row (key="<p><strong>Table 1-1", value="</strong> Overall Design</p>").
    const trimmedPlain = stripHtmlLite(trimmed);
    if (/^Table\s+[\w.-]+\b/i.test(trimmedPlain) || OVERALL_DESIGN_INTRO_RE.test(trimmedPlain)) continue;

    // Markdown / pipe row: | Parameter | Details |
    if (trimmed.includes('|') && /^\|?.+\|.+\|?$/.test(trimmed)) {
      const cells = trimmed
        .split('|')
        .map((c) => c.trim())
        .filter(Boolean);
      if (
        cells.length >= 2 &&
        !/^parameter$/i.test(cells[0]) &&
        !/^details?$/i.test(cells[1])
      ) {
        pendingParent = null;
        pushRow(cells[0], cells.slice(1).join(' | '));
      }
      continue;
    }

    const indent = raw.match(/^(\s*)/)?.[1]?.length || 0;
    const kv = trimmed.match(/^([^:]{2,80}):\s*(.*)$/);
    if (!kv) {
      pendingParent = null;
      continue;
    }

    const key = kv[1].trim();
    const value = (kv[2] || '').trim();

    if (indent >= 2 && pendingParent) {
      pushRow(`${pendingParent} — ${key}`, value);
      continue;
    }

    if (!value) {
      pendingParent = key;
      continue;
    }

    pendingParent = null;
    pushRow(key, value);
  }

  return rows;
};

const DEFAULT_OVERALL_DESIGN_KEYS = [
  'Intervention',
  'Population Type',
  'Population Diagnosis or Condition',
  'Control Type',
  'Randomization',
  'Control Description',
  'Population Age — Minimum',
  'Population Age — Maximum',
  'Site Distribution and Geographic Scope',
  'Intervention Model',
  'Intervention Assignment Method',
  'Master Protocol Indicator',
  'Drug/Device Combination Product Indicator',
  'Adaptive Trial Design Indicator',
  'Number of Arms',
  'Trial Blind Schema',
  'Blinded Roles',
  'Number of Participants',
  'Duration — Total planned duration of trial intervention for each participant',
  'Duration — Total planned duration of trial participation for each participant',
  'Committees',
  'Independent Committees',
];

const mainSectionNumberFromTitle = (sectionTitle) => {
  const m = String(sectionTitle || '').trim().match(/(\d+)/);
  return m ? m[1] : '1';
};

const stripHtmlLite = (s) =>
  String(s || '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const defaultTableTitleFromSection = (sectionTitle, ordinal) => {
  const base =
    String(sectionTitle || 'Protocol')
      .replace(/^\d+(?:\.\d+)*\.?\s*/, '')
      .trim() || 'Protocol Content';
  return ordinal === 1 ? base : `${base} (${ordinal})`;
};

/** Mid-sentence topic casing (fixes ALL-CAPS → "pROTOCOL SUMMARY"). */
const toMidSentenceTopic = (text) => {
  let t = String(text || '').trim();
  if (!t) return t;
  const letters = t.replace(/[^A-Za-z]/g, '');
  const upperRatio = letters
    ? letters.replace(/[^A-Z]/g, '').length / letters.length
    : 0;
  if (upperRatio > 0.55) {
    t = t.toLowerCase();
  } else {
    // Title Case caption ("Trial Stopping Rules and …") → sentence case, keeping
    // acronyms and internally-capitalized names (PK, BG-C137, Kaplan-Meier) as-is.
    t = t
      .split(/(\s+)/)
      .map((word) => (/^[A-Z][a-z'’]*[.,;:]?$/.test(word) ? word.toLowerCase() : word))
      .join('');
  }
  return t;
};

/**
 * Build a title/content-specific in-text cross-reference.
 * Preferred style (label leads):
 *   "Table 9-1 summarizes the reporting requirements for safety events in this study."
 *   "Table 1-1 summarizes the key aspects of the overall trial design."
 *   "Figure 1-1 shows the trial schema."
 */
export const buildContextualCrossReference = (kind, label, title) => {
  let topic = stripHtmlLite(title).replace(/\.+$/, '').trim();
  topic = topic.replace(/^(Table|Figure)\s+\d+(?:[.-]\d+)*\s*[:.-]?\s*/i, '').trim();
  topic = topic.replace(/^(Summary|Overview|List)\s+of\s+/i, '').trim();
  topic = topic.replace(/^(The|A|An)\s+/i, '').trim();

  // Never use the parent Protocol Summary section name as the table topic
  if (/^protocol\s+summary$/i.test(topic) || /^overall\s+design$/i.test(topic)) {
    return kind === 'figure'
      ? `${label} shows the overall trial design.`
      : `${label} summarizes the key aspects of the overall trial design.`;
  }

  if (!topic) {
    return kind === 'figure'
      ? `${label} shows the study design.`
      : `${label} summarizes the key information for this section.`;
  }

  const topicMid = toMidSentenceTopic(topic);
  const article = /^(the|a|an)\s/i.test(topicMid) ? '' : 'the ';

  if (kind === 'figure') {
    let verb = 'shows';
    if (/schema|diagram|flow/i.test(topic)) verb = 'shows';
    else if (/schedule|timeline/i.test(topic)) verb = 'presents';
    return `${label} ${verb} ${article}${topicMid}.`;
  }

  let verb = 'summarizes';
  if (/glossary|abbreviation|definition|list of/i.test(topic)) verb = 'lists';
  else if (/schedule|timing|visit|soa/i.test(topic)) verb = 'presents';
  else if (/criteria|requirement|reporting|assessment|parameter/i.test(topic)) verb = 'summarizes';

  let sentence = `${label} ${verb} ${article}${topicMid}`;
  if (
    !/\b(this study|this trial|this protocol)\b/i.test(sentence) &&
    !/\bfor\s+[A-Z]{2,}[A-Z0-9-]{2,}\b/.test(topic) &&
    /reporting|safety|requirement|assessment|endpoint|adverse|laboratory|population|eligibility/i.test(
      topic
    )
  ) {
    sentence += ' in this study';
  }
  return `${sentence}.`;
};

const findHtmlAndMarkdownTables = (text) => {
  const tables = [];
  const htmlRe = /<table[\s\S]*?<\/table>/gi;
  let m;
  while ((m = htmlRe.exec(text)) !== null) {
    tables.push({ start: m.index, end: m.index + m[0].length, kind: 'table' });
  }
  if (tables.length) return tables;

  const lines = text.replace(/\r\n/g, '\n').split('\n');
  let offset = 0;
  for (let i = 0; i < lines.length; i += 1) {
    const cur = lines[i].trim();
    const next = (lines[i + 1] || '').trim();
    if (/^\|.*\|$/.test(cur) && /^\|?\s*:?-{3,}/.test(next)) {
      const start = offset;
      let end = offset + lines[i].length + 1 + lines[i + 1].length;
      let j = i + 2;
      while (j < lines.length && /^\|.*\|$/.test(lines[j].trim())) {
        end += 1 + lines[j].length;
        j += 1;
      }
      tables.push({ start, end, kind: 'table' });
      for (let k = i; k < j; k += 1) offset += lines[k].length + 1;
      i = j - 1;
      continue;
    }
    offset += lines[i].length + 1;
  }
  return tables;
};

const findHtmlFigures = (text) => {
  const figures = [];
  const figureRe = /<figure[\s\S]*?<\/figure>/gi;
  let m;
  while ((m = figureRe.exec(text)) !== null) {
    figures.push({ start: m.index, end: m.index + m[0].length, kind: 'figure' });
  }
  if (figures.length) return figures;

  // Standalone images treated as figures when no <figure> wrappers
  const imgRe = /<img\b[^>]*>/gi;
  while ((m = imgRe.exec(text)) !== null) {
    figures.push({ start: m.index, end: m.index + m[0].length, kind: 'figure' });
  }
  return figures;
};

const rewriteInformalMediaRefs = (before, label, kind) => {
  const noun = kind === 'figure' ? 'figure' : 'table';
  let out = String(before || '');
  out = out.replace(
    new RegExp(`\\blisted\\s+in\\s+the\\s+(?:following\\s+)?${noun}(?:\\s+below)?\\b`, 'gi'),
    `listed in ${label}`
  );
  out = out.replace(
    new RegExp(
      `\\b(summarised|summarized|provided|described|presented)\\s+in\\s+the\\s+(?:following\\s+)?${noun}(?:\\s+below)?\\b`,
      'gi'
    ),
    (_m, verb) => `${verb} in ${label}`
  );
  out = out.replace(
    new RegExp(
      `\\bas\\s+shown\\s+in\\s+(?:the\\s+)?(?:following\\s+)?${noun}(?:\\s+(?:below|above))?\\b`,
      'gi'
    ),
    `as shown in ${label}`
  );
  out = out.replace(
    new RegExp(
      `\\b(?:see|refer\\s+to)\\s+(?:the\\s+)?(?:following\\s+)?${noun}(?:\\s+(?:below|above|following))?\\b`,
      'gi'
    ),
    `see ${label}`
  );
  out = out.replace(
    new RegExp(`\\bthe\\s+(?:following\\s+)?${noun}\\s+below\\b`, 'gi'),
    label
  );
  // Generic stock phrases that should be replaced with a title-specific sentence
  // Do NOT rewrite the mandatory Overall Design intro ("…summarised below")
  out = out.replace(
    new RegExp(
      `<p>\\s*Key details are summari[sz]ed in ${kind === 'figure' ? 'Figure' : 'Table'}\\s+\\d+(?:[.-]\\d+)*\\.?\\s*</p>`,
      'gi'
    ),
    '\n'
  );
  return out;
};

const captionLineRegex = (kind) => {
  const word = kind === 'figure' ? 'Figure' : 'Table';
  return new RegExp(
    `(?:^|\\n)(?:<p[^>]*>\\s*)?(?:<strong>\\s*)?${word}\\s+(\\d+(?:[.-]\\d+)*)\\s*[:.]?\\s*([^<\\n]*)(?:\\s*</strong>)?(?:\\s*</p>)?\\s*$`,
    'i'
  );
};

/**
 * Global/multiline variant of captionLineRegex for *stripping* every prior caption occurrence
 * before inserting a fresh one. The base regex is single-match and end-of-string-anchored (no
 * 'g'/'m' flags), so repeated processing passes (regenerate, re-render, re-save) only ever
 * cleaned up the single trailing caption — any earlier duplicate captions left over from a
 * previous pass accumulated forever instead of being replaced. This is the root cause of
 * "Table N: Title" appearing 2-3+ times for the same table.
 */
const captionLineRegexGlobal = (kind) => {
  const word = kind === 'figure' ? 'Figure' : 'Table';
  return new RegExp(
    `(?:^|\\n)(?:<p[^>]*>\\s*)?(?:<strong>\\s*)?${word}\\s+(\\d+(?:[.-]\\d+)*)\\s*[:.]?\\s*([^<\\n]*)(?:\\s*</strong>)?(?:\\s*</p>)?\\s*$`,
    'gim'
  );
};

// The caption regex also matches the model's own cross-reference sentence
// ("Table 4-1 summarizes the trial stopping rules…"), and an earlier pass then
// stored that sentence's predicate as the caption ("Table 4-1: summarizes the …")
// and built "…summarizes the summarizes the…". Text after the label that starts
// with a cross-reference verb is therefore a sentence, not a title: the title is
// what follows the verb(s). Also repairs captions already stored in that form.
const CROSS_REF_VERB_LEAD_RE =
  /^(?:(?:summari[sz]es|presents|lists|shows|provides|describes|outlines|details|displays|contains|gives)\s+(?:the\s+|a\s+|an\s+)?)+/i;
const TITLE_SMALL_WORDS = new Set([
  'a', 'an', 'the', 'and', 'or', 'for', 'nor', 'but', 'per', 'so', 'yet', 'to', 'of', 'in', 'on', 'at', 'by', 'with', 'from', 'as', 'vs',
]);

const toCaptionTitleCase = (text) =>
  String(text || '')
    .split(/(\s+)/)
    .map((word, i) => {
      if (/^\s+$/.test(word) || /[A-Z]/.test(word.slice(1))) return word; // spacing, or acronym/mixed case
      const lower = word.toLowerCase();
      if (i > 0 && TITLE_SMALL_WORDS.has(lower)) return lower;
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join('');

const captionTitleFromText = (text) => {
  const plain = stripHtmlLite(text).trim();
  if (!CROSS_REF_VERB_LEAD_RE.test(plain)) return plain;
  const topic = plain
    .replace(CROSS_REF_VERB_LEAD_RE, '')
    .replace(/\s+in\s+this\s+(?:study|trial|protocol)\s*\.?$/i, '')
    .replace(/\.+$/, '')
    .trim();
  return toCaptionTitleCase(topic);
};

const extractTitleFromTableHtml = (tableHtml) => {
  const html = String(tableHtml || '');
  const ths = [...html.matchAll(/<th\b[^>]*>([\s\S]*?)<\/th>/gi)]
    .map((m) => stripHtmlLite(m[1]))
    .filter(Boolean)
    .filter((t) => !/^(parameter|details|notes?|#|no\.?)$/i.test(t));
  if (ths.length >= 2 && ths.length <= 6) {
    return ths.join(', ');
  }
  if (ths.length === 1 && ths[0].length > 8) return ths[0];
  return '';
};

const processMediaBlocks = (original, sectionTitle, blocks, kind, startOrdinal = 0) => {
  if (!blocks.length) return original;

  const main = mainSectionNumberFromTitle(sectionTitle);
  const captionRe = captionLineRegex(kind);
  const kindWord = kind === 'figure' ? 'Figure' : 'Table';
  const escNum = (n) => String(n).replace(/[.-]/g, '\\$&');

  let result = '';
  let cursor = 0;

  blocks.forEach((block, idx) => {
    // startOrdinal continues numbering from earlier sections that share this main
    // section number (1.2 Trial Schema after 1. Protocol Summary) — see
    // computeMediaNumberOffsets.
    const ordinal = startOrdinal + idx + 1;
    let before = original.slice(cursor, block.start);
    const mediaBlock = original.slice(block.start, block.end);
    const labelNum = `${main}-${ordinal}`;
    const label = `${kindWord} ${labelNum}`;

    const captionMatch = before.match(captionRe);
    // When no clean caption line precedes the media block, recover the title from the
    // existing "Table N-M …" blocks this pass is about to replace — a real caption
    // ("Table 4-1: Trial Stopping Rules") first, else the topic of a cross-reference
    // sentence or of a caption stored in sentence form ("Table 4-1: summarizes the …").
    let recoveredTitle = '';
    if (!captionMatch) {
      const labelPrefixRe = new RegExp(`^${kindWord}\\s+(?:${escNum(labelNum)}|${main}-\\d+)\\b\\s*[:.]?\\s*`, 'i');
      const labelTexts = [...before.matchAll(/<(p|h[1-6]|div|li)\b[^>]*>([\s\S]*?)<\/\1>/gi)]
        .map((m) => stripHtmlLite(m[2]).trim())
        .filter((t) => labelPrefixRe.test(t))
        .map((t) => t.replace(labelPrefixRe, '').trim())
        .filter(Boolean);
      recoveredTitle =
        labelTexts.find((t) => !CROSS_REF_VERB_LEAD_RE.test(t)) ||
        labelTexts.map(captionTitleFromText).find(Boolean) ||
        '';
    }
    let title = captionMatch
      ? captionTitleFromText(captionMatch[2]) || defaultTableTitleFromSection(sectionTitle, idx + 1)
      : recoveredTitle || defaultTableTitleFromSection(sectionTitle, idx + 1);

    const isOverallDesignContext =
      /(?:^|\n)(?:#{1,6}\s*|<(?:h[1-6]|p|strong)[^>]*>\s*)?(?:\d+(?:\.\d+)*\s+)?Overall\s+Design\b/i.test(
        before
      ) || /overall\s+design/i.test(sectionTitle);

    const isParameterDetailsTable =
      kind === 'table' &&
      /<th\b[^>]*>\s*Parameter\s*<\/th>/i.test(mediaBlock) &&
      /<th\b[^>]*>\s*Details\s*<\/th>/i.test(mediaBlock);

    // Prefer alt/title from <img> when caption title is weak
    if (kind === 'figure' && (!captionMatch || !stripHtmlLite(captionMatch[2]))) {
      const alt = mediaBlock.match(/\balt=["']([^"']+)["']/i);
      const imgTitle = mediaBlock.match(/\btitle=["']([^"']+)["']/i);
      title = (alt && alt[1]) || (imgTitle && imgTitle[1]) || title;
    }

    // Prefer richer topic from table headers when caption is just the section name
    if (kind === 'table' && !isOverallDesignContext && !isParameterDetailsTable) {
      const fromHeaders = extractTitleFromTableHtml(mediaBlock);
      const sectionDefault = defaultTableTitleFromSection(sectionTitle, 1);
      const titleIsWeak =
        (!captionMatch && !recoveredTitle) ||
        (captionMatch && !stripHtmlLite(captionMatch[2])) ||
        stripHtmlLite(title).toLowerCase() === sectionDefault.toLowerCase() ||
        /^protocol\s+summary$/i.test(stripHtmlLite(title)) ||
        stripHtmlLite(title).length < 12;
      if (fromHeaders && titleIsWeak) {
        title = fromHeaders;
      }
    }

    // 1.1.2 Overall Design Parameter|Details table — force hardcoded intro + cross-ref + caption
    if (isOverallDesignContext || (isParameterDetailsTable && /protocol\s+summary/i.test(sectionTitle))) {
      title = 'Overall Design';
    }
    if (/^protocol\s+summary$/i.test(stripHtmlLite(title))) {
      title = isParameterDetailsTable || isOverallDesignContext ? 'Overall Design' : title;
    }

    const forceOverallDesignHardcoded =
      kind === 'table' &&
      (isOverallDesignContext ||
        (isParameterDetailsTable && /protocol\s+summary|overall\s+design/i.test(sectionTitle)));

    before = rewriteInformalMediaRefs(before, label, kind);
    // Strip every prior caption occurrence (not just the trailing one) before re-inserting.
    before = before.replace(captionLineRegexGlobal(kind), '\n').replace(/\n{3,}/g, '\n\n');

    // Robustly strip ANY existing caption/cross-reference paragraph for this specific
    // label (e.g. "Table 1-1: Overall Design" or "Table 1-1 summarizes the ..."),
    // matched on stripped plain text rather than anchored raw-HTML shape. This step
    // (and the fresh caption/cross-ref appended below) needs to be idempotent because
    // finalizeGeneratedProtocolOutput re-runs on every display/export, not just once at
    // generation time — if stored markup has drifted slightly from what a prior pass
    // inserted, a shape-anchored regex can miss it, leaving a stale duplicate behind
    // while a fresh one still gets appended.
    // Also matches a stale label of this main section ("Table 1-1" stored before this
    // table was renumbered to 1-2), so the old caption/cross-reference is replaced.
    const labelLineRe = new RegExp(`^${kindWord}\\s+(?:${escNum(labelNum)}|${main}-\\d+)\\b`, 'i');
    // Match by block-tag boundary rather than by newline: sibling block elements are
    // frequently serialized with no '\n' between them (e.g. "<p>Table 1-1: Overall
    // Design</p><p>Next paragraph</p>" on one physical line), which defeats a
    // line-anchored regex even though each block is individually well-formed.
    before = before.replace(
      /<(p|h[1-6]|div|li)\b([^>]*)>([\s\S]*?)<\/\1>/gi,
      (match, _tag, _attrs, inner) => (labelLineRe.test(stripHtmlLite(inner).trim()) ? '' : match)
    );
    // Fallback for a bare (unwrapped) caption line that isn't inside any block tag at all.
    before = before
      .split('\n')
      .map((line) => (labelLineRe.test(stripHtmlLite(line).trim()) ? '' : line))
      .join('\n')
      .replace(/\n{3,}/g, '\n\n');

    let lead = before.trimEnd();

    if (forceOverallDesignHardcoded) {
      // Strip any stray copy of the old "Key aspects…" intro line (previously mandatory,
      // now dropped — it duplicated the cross-reference sentence below) left over in
      // already-generated content, then rebuild as the standard cross-ref + caption pair
      // every other table in the document gets.
      lead = lead.replace(
        /(?:<p[^>]*>\s*)?Key aspects of the trial design are summari[sz]ed below\.?(?:\s*<\/p>)?/gi,
        ''
      );
      lead = `${lead}\n\n<p>${overallDesignCrossRefSentence(label)}</p>\n\n<p><strong>${label}:</strong> Overall Design</p>\n\n`;
    } else {
      const beforePlain = stripHtmlLite(lead);
      const hasPreferredStyle = new RegExp(
        `\\b${kindWord}\\s+${escNum(labelNum)}\\s+(?:summarizes|presents|lists|shows)\\b`,
        'i'
      ).test(beforePlain);
      const preferredIsBad = hasPreferredStyle && /protocol\s+summary/i.test(beforePlain);

      if (!hasPreferredStyle || preferredIsBad) {
        const sentence = buildContextualCrossReference(kind, label, title);
        lead = `${lead}\n\n<p>${sentence}</p>`;
      }
      lead = `${lead}\n\n<p><strong>${label}:</strong> ${title}</p>\n\n`;
    }

    result += lead + mediaBlock;
    cursor = block.end;
  });

  result += original.slice(cursor);
  return result.replace(/\n{3,}/g, '\n\n').trim();
};

/**
 * Ensure every table/figure has a formal caption and a title-specific in-text cross-reference.
 * Rewrites informal "table/figure below" phrasing and replaces generic "Key details…" lines.
 */
export const ensureTableFigureCrossReferences = (content, sectionTitle = '', { tableOffset = 0, figureOffset = 0 } = {}) => {
  if (isTransientPlaceholder(content)) return String(content || '');
  let out = String(content || '');
  if (!out.trim()) return out;

  const tables = findHtmlAndMarkdownTables(out);
  if (tables.length) {
    out = processMediaBlocks(out, sectionTitle, tables, 'table', tableOffset);
  }

  const figures = findHtmlFigures(out);
  if (figures.length) {
    out = processMediaBlocks(out, sectionTitle, figures, 'figure', figureOffset);
  }

  return out;
};

/**
 * Force Overall Design (1.1.2) into a Parameter/Details HTML table inside Protocol Summary
 * (or a standalone Overall Design section). Fixes intermittent missing tables after regenerate.
 */
export const ensureOverallDesignTableInContent = (content) => {
  if (isTransientPlaceholder(content)) return String(content || '');
  const original = String(content || '').replace(/\r\n/g, '\n');
  if (!original.trim()) return original;

  const lines = original.split('\n');
  let startIdx = -1;
  for (let i = 0; i < lines.length; i += 1) {
    if (OVERALL_DESIGN_HEADING_RE.test(lines[i].trim())) {
      startIdx = i;
      break;
    }
  }

  // No explicit Overall Design heading: if whole content is design key/values, table-wrap it
  if (startIdx < 0) {
    const looksLikeDesignOnly =
      /Intervention\s*:/i.test(original) &&
      /Population\s+Type\s*:/i.test(original) &&
      !/1\.1\.1\b/.test(original);
    if (!looksLikeDesignOnly) return original;
    if (hasExistingTable(original) && /<table[\s>]/i.test(original)) {
      return ensureOverallDesignIntroBeforeTable(original);
    }

    let rows = parseOverallDesignKeyValueRows(original);
    if (!rows.length && hasExistingTable(original)) {
      const converted = convertContentToDefaultTable(original, 'Overall Design');
      if (/<table[\s>]/i.test(converted)) {
        return ensureOverallDesignIntroBeforeTable(converted);
      }
      return original;
    }
    if (!rows.length) {
      rows = DEFAULT_OVERALL_DESIGN_KEYS.map((key) => ({ key, value: '' }));
    }
    return `1.1.2 Overall Design\n\n${buildHtmlTable(rows)}`;
  }

  let endIdx = lines.length;
  for (let i = startIdx + 1; i < lines.length; i += 1) {
    const t = lines[i].trim();
    if (!t) continue;
    if (OVERALL_DESIGN_HEADING_RE.test(t)) continue;
    if (NEXT_PROTOCOL_HEADING_RE.test(t) && !OVERALL_DESIGN_HEADING_RE.test(t)) {
      endIdx = i;
      break;
    }
  }

  const headingLine = lines[startIdx];
  const bodyLines = lines.slice(startIdx + 1, endIdx);
  const body = bodyLines.join('\n').trim();

  // Already a real HTML table in this subsection — strip any stray legacy intro line
  if (/<table[\s\S]*?>[\s\S]*?<\/table>/i.test(body)) {
    const fixedBody = ensureOverallDesignIntroBeforeTable(body);
    if (fixedBody === body) return original;
    const before = lines.slice(0, startIdx + 1).join('\n');
    const after = lines.slice(endIdx).join('\n');
    return `${before}\n\n${fixedBody}${after ? `\n\n${after}` : ''}`.trim();
  }

  let rows = parseOverallDesignKeyValueRows(body);
  if (!rows.length) {
    // Try converting markdown/prose block via generic converter
    const converted = convertContentToDefaultTable(body, 'Overall Design');
    if (/<table[\s\S]*?>[\s\S]*?<\/table>/i.test(converted)) {
      const before = lines.slice(0, startIdx + 1).join('\n');
      const after = lines.slice(endIdx).join('\n');
      const withIntro = ensureOverallDesignIntroBeforeTable(converted);
      return `${before}\n\n${withIntro}${after ? `\n\n${after}` : ''}`.trim();
    }
    rows = DEFAULT_OVERALL_DESIGN_KEYS.map((key) => ({ key, value: '' }));
  }

  const tableHtml = buildHtmlTable(rows);
  const before = lines.slice(0, startIdx).join('\n');
  const after = lines.slice(endIdx).join('\n');
  const parts = [
    before.trimEnd(),
    headingLine.trim(),
    '',
    tableHtml,
    after.trimStart(),
  ].filter((p, idx, arr) => !(p === '' && (idx === 0 || idx === arr.length - 1)));

  return parts.join('\n').replace(/\n{3,}/g, '\n\n').trim();
};

// Section 3 headings that are "No text intended here (heading only)".
const SECTION3_HEADING_ONLY_RES = [
  /^3(?!\.\d)\.?\s+TRIAL\s+OBJECTIVES/i, // 3. TRIAL OBJECTIVES AND ASSOCIATED ESTIMANDS
  /^3\.1(?!\.?\d)\.?\s+PRIMARY\s+OBJECTIVE/i, // 3.1 Primary Objective(s) and Associated Estimand(s)
];

// Any heading-like line: HTML <hN>, markdown ATX, or a bare numbered heading line.
const HEADING_TOKEN_RE =
  /<[hH]([1-6])\b[^>]*>([\s\S]*?)<\/[hH]\1>|^[ \t]*#{1,6}[ \t]+(.*)$|^[ \t]*(?:<p[^>]*>[ \t]*)?(?:\*\*)?(\d+(?:\.\d+)*\.?[ \t]+[A-Z][^\n<]{0,160}?)(?:\*\*)?[ \t]*(?:<\/p>)?[ \t]*$/gm;

/**
 * Strip body text sitting directly under the Section 3 / 3.1 headings (heading only per
 * template) so content generated before that rule was enforced displays/exports correctly
 * without regenerating. Only removes the run up to the next heading, and never when that
 * run holds a table/figure (e.g. an estimand table placed straight under 3.1). Idempotent.
 */
const stripSection3HeadingOnlyBodies = (content) => {
  let out = String(content || '');
  for (const textRe of SECTION3_HEADING_ONLY_RES) {
    const tokens = [...out.matchAll(HEADING_TOKEN_RE)];
    const idx = tokens.findIndex((m) =>
      textRe.test(stripInlineHtmlTags(m[2] ?? m[3] ?? m[4] ?? '').replace(/\*+/g, '').trim()),
    );
    if (idx === -1 || idx === tokens.length - 1) continue;
    const start = tokens[idx].index + tokens[idx][0].length;
    const end = tokens[idx + 1].index;
    const body = out.slice(start, end);
    if (!body.trim()) continue;
    if (/<table[\s>]|<img[\s>]|<figure[\s>]/i.test(body) || /^\s*\|.*\|\s*$/m.test(body)) continue;
    out = `${out.slice(0, start)}\n\n${out.slice(end)}`;
  }
  return out;
};

// A markdown bullet ("*   Medication errors…") left as plain text inside HTML.
const ASTERISK_ITEM_RE = /^\s*\*\s+(?=\S)/;

/**
 * Turn markdown "* item" bullets the model left inside HTML paragraphs into a real
 * <ul> list — they otherwise render as literal asterisks (and read as footnote
 * markers to the style checks). Two shapes are repaired:
 *   <p>Intro:<br />*   Item one.<br />*   Item two.</p>  → <p>Intro:</p><ul><li>…</li>…</ul>
 *   <p>* Item one.</p><p>* Item two.</p>                → <ul><li>…</li><li>…</li></ul>
 * Only when every line after the first bullet is a bullet (2+ bullets), so prose that
 * merely contains an asterisk is left alone. Idempotent — no "* " remains afterwards.
 */
const repairAsteriskBulletLists = (html) => {
  let out = String(html || '');
  if (!out.includes('*')) return out;
  const stripTags = (s) => String(s).replace(/<[^>]+>/g, '');
  const itemHtml = (s) => s.replace(/^(\s*(?:<[^>]+>\s*)*)\*\s+/, '$1').trim();

  out = out.replace(/<p\b([^>]*)>([\s\S]*?)<\/p>/gi, (match, attrs, inner) => {
    const lines = inner.split(/<br\s*\/?>/i);
    const isItem = (line) => ASTERISK_ITEM_RE.test(stripTags(line));
    // Group consecutive lines into bullet runs and prose runs; blank lines end a run.
    const groups = [];
    lines.forEach((line) => {
      if (!stripTags(line).trim()) {
        groups.push(null);
        return;
      }
      const kind = isItem(line) ? 'item' : 'text';
      const last = groups[groups.length - 1];
      if (last && last.kind === kind) last.lines.push(line);
      else groups.push({ kind, lines: [line] });
    });
    // Only rewrite a real list: some run of 2+ bullets.
    if (!groups.some((g) => g && g.kind === 'item' && g.lines.length >= 2)) return match;
    return groups
      .filter(Boolean)
      .map((g) =>
        g.kind === 'item'
          ? `<ul>${g.lines.map((line) => `<li>${itemHtml(line)}</li>`).join('')}</ul>`
          : `<p${attrs}>${g.lines.join('<br />').trim()}</p>`,
      )
      .join('');
  });

  // (?:(?!<\/p>)[\s\S])* keeps each item inside its own paragraph — a lazy [\s\S]*?
  // could stretch across an ordinary paragraph to reach the next "* " one.
  out = out.replace(/(?:<p\b[^>]*>\s*\*\s+(?:(?!<\/p>)[\s\S])*<\/p>\s*){2,}/gi, (run) => {
    const items = [...run.matchAll(/<p\b[^>]*>\s*\*\s+((?:(?!<\/p>)[\s\S])*)<\/p>/gi)];
    return `<ul>${items.map((m) => `<li>${m[1].trim()}</li>`).join('')}</ul>`;
  });
  return out;
};

/**
 * Post-process generated protocol section output (deterministic, no LLM).
 * Ensures Section 1 Overall Design always has a Parameter | Details table.
 */
export const finalizeGeneratedProtocolOutput = (content, sectionTitle = '', mediaNumbering = {}) => {
  let out = String(content || '');
  if (isTransientPlaceholder(out)) return out;
  out = normalizeCollapsedMarkdownTablePipes(out);
  out = normalizeMarkdownTableHeaderSeparatorRun(out);

  const title = String(sectionTitle || '');
  if (
    /protocol\s+summary/i.test(title) ||
    /^1(\.0)?\b/i.test(title) ||
    /overall\s+design/i.test(title)
  ) {
    out = ensureOverallDesignTableInContent(out);
  }

  if (/^\s*3(?:\.|\s)/.test(title) && /objective|estimand/i.test(title)) {
    out = stripSection3HeadingOnlyBodies(out);
  }

  // All sections: formal Table {section}-{n} captions + in-text cross-references
  out = ensureTableFigureCrossReferences(out, title, mediaNumbering);

  // Repair the two LLM output slips display/export already repair (stray
  // markdown ATX headings mixed into HTML; a tag missing its leading '<',
  // e.g. "li>" instead of "<li>") HERE TOO, at generation/save time — this
  // is the root-cause fix, not just a display-time patch: the backend's
  // Mongoose setter for `output` (sanitizeServerHtml, via sanitize-html)
  // parses "li>text" as a text node (no leading '<' to recognize as a tag)
  // and re-escapes its literal '>' to '&gt;' when serializing — so an
  // unrepaired "li>" saved today becomes an even harder to repair "li&gt;"
  // once persisted. Fixing it before the save request means sanitize-html
  // sees a real, well-formed tag and never touches it. Idempotent with the
  // display/export-layer calls to the same functions, so this is safe to
  // also leave those in place for content saved before this fix existed.
  out = convertStrayMarkdownHeadingsToHtml(out);
  out = repairOrphanedOpeningTags(out);
  out = repairAsteriskBulletLists(out);

  // Grammar guard: sentence-starting words (and the "I" pronoun) should be
  // capitalized — a common LLM output slip. Case-only, idempotent, and safe
  // to run on HTML (see grammarGuards.js), matching every other step above.
  out = applyGrammarGuardsToHtml(out);

  // Character-level style rules the model can't reliably follow (curly quotes,
  // single spacing, e.g./i.e. style, ISMP dose trailing zeros) — see
  // protocolTypography.js. Runs last so it also repairs URLs an older
  // capitalization pass left as "Https://".
  out = applyProtocolTypography(out);

  return out;
};

/**
 * Table/figure numbering offsets for a whole document, so numbering continues across
 * sections that share a main number instead of restarting in each one: "1. Protocol
 * Summary" has Table 1-1, so "1.2 Trial Schema" starts at Table 1-2 and "1.3 Schedule
 * of Activities" after that. Returns one { tableOffset, figureOffset } per section, in
 * the given (document) order, to pass as finalizeGeneratedProtocolOutput's /
 * prepareSectionContentForExport's third argument.
 */
export const computeMediaNumberOffsets = (
  sections = [],
  { getContent = (s) => s?.output || '', getTitle = (s) => s?.title || '' } = {},
) => {
  const running = {};
  return (sections || []).map((section) => {
    const title = getTitle(section);
    const main = mainSectionNumberFromTitle(title);
    if (!running[main]) running[main] = { table: 0, figure: 0 };
    const offsets = { tableOffset: running[main].table, figureOffset: running[main].figure };
    const content = String(getContent(section) || '');
    if (content.trim() && !isTransientPlaceholder(content)) {
      // Count on finalized content: finalizing can add a table (Overall Design).
      const finalized = finalizeGeneratedProtocolOutput(content, title, offsets);
      running[main].table += findHtmlAndMarkdownTables(finalized).length;
      running[main].figure += findHtmlFigures(finalized).length;
    }
    return offsets;
  });
};

/**
 * Normalizes section content to HTML for Word/PDF export.
 * processContentForDisplay keeps markdown tables as raw markdown for ReactMarkdown preview;
 * exports need real <table> markup for docx/jspdf converters.
 */
export const prepareSectionContentForExport = (content, sectionTitle = '', mediaNumbering = {}) => {
  if (isTransientPlaceholder(content)) return '';
  if (!content || !String(content).trim()) return '';

  const sectionRootPrefix = (String(sectionTitle).match(/^(\d+(?:\.\d+)*)/) || [])[1] || null;
  const normalized = finalizeGeneratedProtocolOutput(content, sectionTitle, mediaNumbering);
  // Both fixes below must run BEFORE branching further down — different branches route through
  // processContentForDisplay vs markdownPlainTextToHtml (marked.parse), and only the former
  // understands ```mermaid fences / runs heading-restatement dedup. Canonicalizing here means
  // every downstream branch sees the same normalized content regardless of which one handles it.
  let raw = convertMermaidFencesToPre(
    stripDuplicateHeadingRestatements(prepareProtocolHeadingContent(normalized, sectionTitle)),
  );
  // Repair the same two LLM output slips processContentForDisplay repairs for on-screen
  // preview/edit (glued "## Title ### Next" headings, and a dropped leading '<' like "li>"
  // instead of "<li>") — applied once, upfront, so every branch below (table/<table>,
  // markdownPlainTextToHtml's marked.parse, getTableFirstDisplayContent, and the final
  // processContentForDisplay fallback) sees already-repaired content, since not all of them
  // otherwise run these repairs themselves (markdownPlainTextToHtml's own glue-splitter only
  // splits before a fixed word list, not before another heading marker).
  raw = repairOrphanedOpeningTags(splitGluedMarkdownHeadingMarkers(raw));

  const finish = (html) =>
    promoteProtocolSubsectionHeadingsInHtml(
      joinFragmentedHeadingContinuationInHtml(html, sectionRootPrefix),
      sectionRootPrefix,
    );

  if (/<table[\s>]/i.test(raw)) {
    return finish(processContentForDisplay(raw));
  }

  const hasMarkdownTable =
    /^\s*\|.+\|\s*$/m.test(raw) && /^\s*\|?\s*:?-{3,}/m.test(raw);
  const hasMarkdownHeadings = /^#{1,6}\s/m.test(raw);
  const hasHtmlTags = /<\w+[^>]*>/.test(raw);
  const hasSubsectionNumberLines = /(?:^|\n)\d+\.\d+(?:\.\d+)*\s+\S/m.test(raw);

  if (hasMarkdownTable || hasMarkdownHeadings || !hasHtmlTags || hasSubsectionNumberLines) {
    let md = normalizeCollapsedMarkdownTablePipes(raw);
    md = normalizeMarkdownTableHeaderSeparatorRun(md);
    const html = markdownPlainTextToHtml(md);
    if (html && html.trim()) return finish(html);
  }

  const tableFirst = getTableFirstDisplayContent(raw, sectionTitle);
  if (/<table[\s>]/i.test(tableFirst)) {
    return finish(tableFirst);
  }

  return finish(processContentForDisplay(raw));
};

/**
 * Extract plain text from an HTML element for PDF export (sup/sub, line breaks).
 */
export const extractPlainTextForPdf = (element) => {
  if (!element) return '';

  const walk = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent || '';
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return '';

    const tag = node.tagName?.toLowerCase();
    if (tag === 'sup') {
      const digits = (node.textContent || '').trim();
      return digits ? `^${digits}` : '';
    }
    if (tag === 'sub') {
      const digits = (node.textContent || '').trim();
      return digits ? `_${digits}` : '';
    }
    if (tag === 'br') return '\n';
    if (tag === 'li') {
      return Array.from(node.childNodes).map(walk).join('');
    }

    return Array.from(node.childNodes).map(walk).join('');
  };

  const root = element.nodeType === Node.ELEMENT_NODE ? element : null;
  if (!root) return normalizePdfText(String(element));

  return normalizePdfText(walk(root));
};

/**
 * Prepares text for jsPDF (Helvetica / WinAnsi only).
 * Unicode symbols corrupt PDF output (arrows, math, superscripts, etc.).
 */
export const normalizePdfText = (text) => {
  if (text == null) return '';
  let normalized = String(text);

  // HTML entities that may appear as literal text
  normalized = normalized
    .replace(/&ge;|&GreaterEqual;/gi, '>=')
    .replace(/&le;|&LessEqual;/gi, '<=')
    .replace(/&ne;|&NotEqual;/gi, '!=')
    .replace(/&plusmn;/gi, '+/-')
    .replace(/&times;/gi, 'x')
    .replace(/&divide;/gi, '/')
    .replace(/&mu;/gi, 'u')
    .replace(/&deg;/gi, ' deg')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&rarr;|&rightarrow;/gi, ' -> ')
    .replace(/&larr;/gi, ' <- ')
    .replace(/&ndash;|&mdash;/gi, '-');

  // Mojibake from prior broken PDF / WinAnsi rendering of math symbols
  normalized = normalized
    .replace(/"\s*e(?=\s*[\d(.])/gi, '>=')
    .replace(/"\s*d(?=\s*[\d(.])/gi, '<=')
    .replace(/!\s*['\u2019\u2018](?=\s*[A-Z(])/g, ' -> ');

  // Math and comparison operators (clinical criteria)
  normalized = normalized
    .replace(/\u2265/g, '>=')
    .replace(/\u2264/g, '<=')
    .replace(/\u2260/g, '!=')
    .replace(/\u2261/g, '=')
    .replace(/\u2248/g, '~')
    .replace(/\u00B1/g, '+/-')
    .replace(/\u00D7/g, 'x')
    .replace(/\u00F7/g, '/')
    .replace(/\u2212/g, '-')
    .replace(/\u2219/g, '-')
    .replace(/\u2022/g, '- ')
    .replace(/\u00B7/g, '- ');

  // Superscript digits (e.g. 10⁹/L → 10^9/L)
  const superscriptMap = {
    '\u2070': '0', '\u00B9': '1', '\u00B2': '2', '\u00B3': '3',
    '\u2074': '4', '\u2075': '5', '\u2076': '6', '\u2077': '7', '\u2078': '8', '\u2079': '9',
  };
  normalized = normalized.replace(/[\u2070\u00B9\u00B2\u00B3\u2074-\u2079]/g, (ch) => {
    const digit = superscriptMap[ch];
    return digit != null ? `^${digit}` : ch;
  });

  // Subscript digits
  const subscriptMap = {
    '\u2080': '0', '\u2081': '1', '\u2082': '2', '\u2083': '3', '\u2084': '4',
    '\u2085': '5', '\u2086': '6', '\u2087': '7', '\u2088': '8', '\u2089': '9',
  };
  normalized = normalized.replace(/[\u2080-\u2089]/g, (ch) => {
    const digit = subscriptMap[ch];
    return digit != null ? `_${digit}` : ch;
  });

  // Greek / units common in protocols
  normalized = normalized
    .replace(/\u03BC/g, 'u')
    .replace(/\u03B1/g, 'alpha')
    .replace(/\u03B2/g, 'beta')
    .replace(/\u00B0/g, ' deg')
    .replace(/\u2122/g, '(TM)')
    .replace(/\u00AE/g, '(R)')
    .replace(/\u00A9/g, '(C)');

  // Arrows and flow symbols
  normalized = normalized
    .replace(/[\u2190-\u21FF\u27A0-\u27BF\u2794-\u2799\u21D2\u21D4\u27F6\u27F9]/g, ' -> ')
    .replace(/\s*[-–—]{1,2}>\s*/g, ' -> ');

  // Smart quotes and dashes
  normalized = normalized
    .replace(/[\u2018\u2019\u201A\u201B]/g, "'")
    .replace(/[\u201C\u201D\u201E\u201F]/g, '"')
    .replace(/[\u2013\u2014]/g, '-');

  // Invisible / odd whitespace
  normalized = normalized
    .replace(/[\u00A0\u2000-\u200B\u202F\u205F\u3000\uFEFF]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  // Recover text split into "S c r e e n i n g ( u p t o ... )"
  const parts = normalized.split(/\s+/);
  if (parts.length >= 12) {
    const singleCharCount = parts.filter((part) => part.length === 1).length;
    if (singleCharCount / parts.length >= 0.8) {
      normalized = parts.join('');
      normalized = normalized
        .replace(/(\d)([A-Za-z])/g, '$1 $2')
        .replace(/([A-Za-z])(\()/g, '$1 $2')
        .replace(/\)([A-Za-z])/g, ') $1')
        .replace(/\s*->\s*/g, ' -> ');
    }
  }

  // Drop any remaining non-ASCII characters jsPDF Helvetica cannot render
  normalized = normalized.replace(/[^\x20-\x7E]/g, '');

  // Tidy comparison spacing
  normalized = normalized
    .replace(/\s*>=\s*/g, ' >= ')
    .replace(/\s*<=\s*/g, ' <= ')
    .replace(/\s+/g, ' ')
    .trim();

  return normalized;
};

